import React from 'react';
import { Helmet } from 'react-helmet';
import type { LegalAddendum, LegalDocument } from '../../legal/types';
import { COMPANY } from '../content/company';
import MarketingLayout from '../layout/MarketingLayout';
import LegalBlocks from '../legal/LegalBlocks';
import { renderInline } from '../legal/renderInline';

interface LegalDocumentPageProps {
  document: LegalDocument;
  addendum?: LegalAddendum;
}

/**
 * The shell for /privacy and /terms: the document's own title, its version
 * and hard-coded last-updated date, the intro paragraphs, the numbered
 * sections (Arabic, because the documents cross-reference by number), an
 * optional separately-versioned addendum, and the document history. Heading
 * order is h1 → h2 → h3. The text itself comes from src/legal/*.ts and is a
 * verbatim port of the controlled documents.
 */
export default function LegalDocumentPage({ document, addendum }: LegalDocumentPageProps) {
  return (
    <MarketingLayout activePage="home">
      <Helmet>
        <title>{document.pageTitle}</title>
        <meta name="description" content={document.description} />
        <link rel="canonical" href={`${COMPANY.siteUrl}/${document.id}`} />
      </Helmet>

      <div className="mk-legal">
        <div className="mk-container">
          <header className="mk-legal__head">
            <p className="t-details t-muted">{document.kicker}</p>
            <h1 className="mk-legal__title t-display">{document.title}</h1>
            <p className="mk-legal__meta t-details t-muted">
              Melio Health Solutions Ltd · Version {document.version} · Last updated {document.updated}
            </p>
            {document.intro.length > 0 && (
              <div className="mk-legal__intro mk-legal__body t-body">
                <LegalBlocks blocks={document.intro} />
              </div>
            )}
          </header>

          <div className="mk-legal__sections">
            {document.sections.map((section) => (
              <article key={section.number} className="mk-legal__section" id={`section-${section.number}`}>
                <span className="mk-legal__num t-details" aria-hidden="true">
                  {section.number}
                </span>
                <div>
                  <h2 className="mk-legal__h2">{section.title}</h2>
                  <div className="mk-legal__body t-body">
                    <LegalBlocks blocks={section.blocks} />
                  </div>
                </div>
              </article>
            ))}
          </div>

          {addendum && (
            <section id={addendum.id} className="mk-legal__block" aria-labelledby={`${addendum.id}-title`}>
              <p className="t-details t-muted">{addendum.kicker}</p>
              <h2 id={`${addendum.id}-title`} className="mk-legal__h2">
                {addendum.title}
              </h2>
              <p className="mk-legal__meta t-details t-muted">
                Version {addendum.version} &middot; {addendum.date}
              </p>
              <div className="mk-legal__body t-body">
                <LegalBlocks blocks={addendum.blocks} />
              </div>
            </section>
          )}

          <div className="mk-legal__block">
            <h2 className="mk-legal__h2">Document history</h2>
            <div className="mk-legal__table-wrap">
              <table className="mk-legal__table t-body">
                <thead>
                  <tr>
                    <th scope="col" className="t-details">Version</th>
                    <th scope="col" className="t-details">Date</th>
                    <th scope="col" className="t-details">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {document.history.map((row) => (
                    <tr key={row.version}>
                      <td style={{ whiteSpace: 'nowrap' }}>{row.version}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{row.date}</td>
                      <td>{renderInline(row.change)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
